import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Translation, TranslocoLoader } from '@jsverse/transloco';
import { lastValueFrom, retry, throwError, timer } from 'rxjs';
import { UserService } from '../services/user.service';
import { HttpCacheManager, withCache } from '@ngneat/cashew';
import { TranslocoCacheTime } from './transloco-cache.const';

@Injectable({ providedIn: 'root' })
export class TranslocoHttpLoader implements TranslocoLoader {
    constructor(
        private httpClient: HttpClient,
        private userService: UserService,
        private cacheManager: HttpCacheManager
    ) {}

    async getTranslation(lang: string): Promise<Translation> {
        let language: string;
        let module: string;
        if (lang.includes('/')) {
            const parts = lang.split('/');
            module = parts[0] ?? 'page';
            language = parts[1] ?? lang;
        } else {
            language = lang;
            module = 'page';
        }

        if (module === 'codext') {
            return await lastValueFrom(
                this.httpClient.get<Translation>(
                    `/assets/i18n/${language}.json`
                )
            );
        }

        const cacheKey = this.buildCacheKey(module, language);
        const cached = await this.fetchModuleTranslations(
            module,
            language,
            cacheKey,
            true
        );
        if (this.isValidTranslation(cached)) {
            return cached;
        }

        // The cache delivered (or just stored) an empty/invalid payload — drop it
        // and try again live so users don't keep seeing literal translation keys
        // until the 1-day TTL expires.
        this.cacheManager.delete(cacheKey);
        const fresh = await this.fetchModuleTranslations(
            module,
            language,
            cacheKey,
            false
        );
        return this.isValidTranslation(fresh) ? fresh : ({} as Translation);
    }

    private buildCacheKey(module: string, language: string): string {
        // Keying by uid prevents an empty/anonymous response from polluting
        // the cache for a logged-in user (and vice versa).
        const uid = this.userService?.user?.uid ?? 'anon';
        return `translate-${module}-${language}-${uid}`;
    }

    private async fetchModuleTranslations(
        module: string,
        language: string,
        cacheKey: string,
        useCache: boolean
    ): Promise<Translation | null> {
        const uid = this.userService?.user?.uid;
        const baseUrl = `hippo/navigator.php?page=pages/system/System_translations.php&page_type=json${
            uid ? `&uid=${uid}` : ''
        }`;
        const formData = new FormData();
        formData.append('iso_code', language);
        formData.append('module', module);

        try {
            const options = useCache
                ? {
                      context: withCache({
                          ttl: TranslocoCacheTime,
                          key: cacheKey,
                      }),
                  }
                : {};
            const response = await lastValueFrom(
                this.httpClient
                    .post<{ result?: Translation }>(baseUrl, formData, options)
                    .pipe(
                        retry({
                            count: 2,
                            delay: (err: unknown, retryCount: number) => {
                                // Don't retry auth/permission errors — only transient ones.
                                if (err instanceof HttpErrorResponse) {
                                    const status = err.status;
                                    if (status >= 400 && status < 500) {
                                        return throwError(() => err);
                                    }
                                }
                                return timer(500 * retryCount);
                            },
                        })
                    )
            );
            const result = response?.result ?? null;
            if (useCache && !this.isValidTranslation(result)) {
                this.cacheManager.delete(cacheKey);
            }
            return result;
        } catch (error) {
            if (useCache) {
                this.cacheManager.delete(cacheKey);
            }
            return null;
        }
    }

    private isValidTranslation(value: unknown): value is Translation {
        return (
            !!value &&
            typeof value === 'object' &&
            !Array.isArray(value) &&
            Object.keys(value as object).length > 0
        );
    }
}
